/**
 * THE FROZEN CHARACTER CONTRACT — PRODUCTION AUTHORED MASTER — GENERATED, DO NOT HAND-EDIT.
 *
 * Regenerate with `node scripts/freeze-skeleton-contract.mjs`, which is a
 * deliberate contract change: the version string is a hash of this table, so
 * rewriting it invalidates every asset that was validated against the old one.
 *
 * Source: packages/client/public/characters/player-master/player-master-lod0.glb
 *
 * This is the PRODUCTION ATHLETE, authored in Blender by
 * `assets/player-master/pipeline/build_master_player.py` and merged into ONE
 * SkinnedMesh with ONE atlas material by `optimize_runtime.py`. The table is
 * read off the OPTIMISED export that actually ships, not off the 19-material
 * authoring export (archived, see productionAssetManifest.ts). 74 bones: 60
 * deform + 12 non-deform `socket_*`, rooted at a dedicated root-motion bone,
 * authored in METRES at 1 unit = 1 m.
 *
 * It carries NO animation clips. The 13 gameplay clips live on the temporary
 * Mixamo rig, whose 65 bone names do not intersect this table at any point,
 * so not one of their 2,535 tracks resolves here. Swapping the visual without
 * re-authoring the clips against THIS table yields a bind-pose statue, and a
 * runtime bone remap is explicitly refused — see docs/SKELETON_SWAP.md.
 *
 * WHY A TABLE AND NOT PROSE. "Do not rename or re-parent bones" is unenforceable
 * as a sentence and trivially enforceable as 74 rows of name, parent, bind
 * transform and inverse bind. A renamed bone, a re-parented limb, a lengthened
 * forearm, a scaled bone and a nudged rest pose all move a number here, and a
 * moved number is a loud load failure instead of a character that animates
 * subtly wrong.
 */

import type { ClipSource, FrozenBone } from './skeletonContract';
export type { ClipSource, FrozenBone };

/** Hash of the bone identity table plus the clip-name set. The contract version. */
export const AUTHORED_SKELETON_CONTRACT_VERSION = "player-master-1.a4b23dda95663eeb";

/**
 * Hash of the FULL table — inverse binds and deform flags included. A re-export
 * that rebaked the bind from a posed skeleton moves this and not the version.
 */
export const AUTHORED_CONTRACT_BIND_HASH = "1518f7a9b9d43d91";

export const AUTHORED_CONTRACT_SKELETON_ASSET = "player-master-lod0.glb";

export const AUTHORED_CONTRACT_SKELETON_ASSET_PATH = "packages/client/public/characters/player-master/player-master-lod0.glb";

/** Files that must carry a bit-identical skeleton. Asserted by test. */
export const AUTHORED_CONTRACT_SIBLING_ASSETS: readonly string[] = ["packages/client/public/characters/player-master/player-master-lod1.glb","packages/client/public/characters/player-master/player-master-lod2.glb","packages/client/public/characters/player-master/player-master-lod3.glb"];

export const AUTHORED_CONTRACT_ROOT_BONE = "root";

export const AUTHORED_CONTRACT_BONE_COUNT = 74;

/** Bones that carry skin weight. The rest are sockets, helpers or the root. */
export const AUTHORED_CONTRACT_DEFORM_BONE_COUNT = 61;

/** Non-deform attachment points. They ride the animation; never keyframe them. */
export const AUTHORED_CONTRACT_SOCKETS: readonly string[] = ["socket_ball_control","socket_pelvis","socket_head","socket_chest","socket_foot_l","socket_toe_l","socket_knee_l","socket_tackle_l","socket_foot_r","socket_toe_r","socket_knee_r","socket_tackle_r"];

/**
 * Authoring units of every number in this table, and the scale on the armature
 * node that converts them to metres. `cm` under an 0.01 armature and `m` under
 * a 1.0 armature are 100x apart, which is why position tracks from one rig
 * cannot be replayed on the other even where a name happens to match.
 */
export const AUTHORED_CONTRACT_UNITS = "m";

export const AUTHORED_CONTRACT_ARMATURE_NODE = "Player_Master";

export const AUTHORED_CONTRACT_ARMATURE_SCALE: readonly [number, number, number] = [1,1,1];

/** Max skin influences per vertex in the shipped file. */
export const AUTHORED_CONTRACT_MAX_INFLUENCES = 4;

/** Meshes in the shipped file. The runtime path wants exactly one. */
export const AUTHORED_CONTRACT_MESH_COUNT = 1;

/** Every clip the contract guarantees. A drop here breaks the state machine. */
export const AUTHORED_CONTRACT_CLIPS: readonly string[] = ["Idle","Backpedal","Header","Jog","Pass","Penalty","Sprint","StrafeLeft","StrafeRight","ThrowIn","Run","Walk"];

/** Where those clips live, when they are not inside the skeleton GLB itself. */
export const AUTHORED_CONTRACT_CLIP_SOURCES: readonly ClipSource[] = [
  {
    "id": "Idle",
    "file": "packages/client/public/characters/football/mocap/Idle.glb",
    "clipName": "Animation",
    "duration": 10.2667,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 44.9,
    "horizontalMotionRangeMM": 0,
    "loopDriftMM": 4.52,
    "originFile": "tools/anim/author/clips/idle.py",
    "originAction": null,
    "originRig": "player-master-74",
    "processing": [
      "keyed by hand on the 74-bone master in Blender, not retargeted",
      "every gate in build_clip.py PASSED before promotion",
      "rest skeleton aligned to the master by scripts/align-clip-skeletons.mjs",
      "dead-frame-retimed"
    ],
    "frames": 90,
    "fps": 29.667,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "Backpedal",
    "file": "packages/client/public/characters/football/mocap/M_Backpedal.glb",
    "clipName": "Animation",
    "duration": 0.6,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 39.6,
    "horizontalMotionRangeMM": 41.4,
    "loopDriftMM": 0,
    "originFile": "mixamo-skeleton-animations.glb",
    "originAction": "Backpedal",
    "originRig": "mixamo",
    "processing": [
      "facing-normalized",
      "reference-pose-retarget",
      "twist-distributed",
      "sole-ground-solve",
      "pelvis-weight-preserved",
      "hips-in-place-detrended"
    ],
    "frames": 18,
    "fps": 30,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "Header",
    "file": "packages/client/public/characters/football/mocap/M_Header.glb",
    "clipName": "Animation",
    "duration": 1.9333,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 0,
    "verticalMotionRangeMM": 0,
    "horizontalMotionRangeMM": 0,
    "loopDriftMM": 0,
    "originFile": "mixamo-skeleton-animations.glb",
    "originAction": "Header",
    "originRig": "mixamo",
    "processing": [
      "facing-normalized",
      "rest-offset-retarget",
      "hips-in-place"
    ],
    "frames": 58,
    "fps": 30,
    "plantWindows": {
      "left": [
        [
          0,
          0.3
        ],
        [
          0.8,
          0.9333
        ],
        [
          1.1,
          1.5
        ],
        [
          1.7333,
          1.9
        ]
      ],
      "right": [
        [
          0,
          0.2333
        ],
        [
          0.7667,
          0.8667
        ],
        [
          1.0333,
          1.1667
        ],
        [
          1.3333,
          1.5
        ],
        [
          1.7333,
          1.9
        ]
      ]
    },
    "settledTime": 1.7333
  },
  {
    "id": "Jog",
    "file": "packages/client/public/characters/football/mocap/M_Jog.glb",
    "clipName": "Animation",
    "duration": 1.0104,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 97.7,
    "horizontalMotionRangeMM": 66.3,
    "loopDriftMM": 0,
    "originFile": "Jog Forward (1).fbx",
    "originAction": "Jog Forward (1)",
    "originRig": "mixamo",
    "processing": [
      "reference-pose-retarget",
      "sole-ground-solve",
      "pelvis-weight-preserved",
      "hips-in-place-detrended",
      "pop-smoothed"
    ],
    "frames": 26,
    "fps": 30,
    "plantWindows": {
      "left": [],
      "right": [
        [
          0.0333,
          0.0333
        ]
      ]
    },
    "settledTime": null
  },
  {
    "id": "Pass",
    "file": "packages/client/public/characters/football/mocap/M_Pass.glb",
    "clipName": "Animation",
    "duration": 1.6333,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 72.7,
    "horizontalMotionRangeMM": 250.3,
    "loopDriftMM": 0,
    "originFile": "Soccer Pass.fbx",
    "originAction": "Soccer Pass",
    "originRig": "mixamo",
    "processing": [
      "reference-pose-retarget",
      "sole-ground-solve",
      "pelvis-weight-preserved",
      "hips-in-place-detrended",
      "pop-smoothed",
      "dead-frame-retimed"
    ],
    "frames": 49,
    "fps": 29.388,
    "plantWindows": {
      "left": [
        [
          0.034,
          0.1021
        ],
        [
          0.3743,
          0.5785
        ],
        [
          0.6465,
          1.225
        ],
        [
          1.5312,
          1.6673
        ]
      ],
      "right": [
        [
          0.034,
          0.2382
        ],
        [
          1.225,
          1.6673
        ]
      ]
    },
    "settledTime": 1.5312
  },
  {
    "id": "Penalty",
    "file": "packages/client/public/characters/football/mocap/M_Penalty.glb",
    "clipName": "Animation",
    "duration": 1.5333,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 0,
    "verticalMotionRangeMM": 0,
    "horizontalMotionRangeMM": 0,
    "loopDriftMM": 0,
    "originFile": "mixamo-skeleton-animations.glb",
    "originAction": "Penalty",
    "originRig": "mixamo",
    "processing": [
      "facing-normalized",
      "rest-offset-retarget",
      "hips-in-place"
    ],
    "frames": 46,
    "fps": 30,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "Sprint",
    "file": "packages/client/public/characters/football/mocap/M_Sprint.glb",
    "clipName": "Animation",
    "duration": 0.688,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 91.4,
    "horizontalMotionRangeMM": 33.1,
    "loopDriftMM": 0,
    "originFile": "Running (2).fbx",
    "originAction": "Running (2)",
    "originRig": "mixamo",
    "processing": [
      "reference-pose-retarget",
      "sole-ground-solve",
      "pelvis-weight-preserved",
      "hips-in-place-detrended",
      "pop-smoothed",
      "dead-frame-retimed"
    ],
    "frames": 17,
    "fps": 28.235,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "StrafeLeft",
    "file": "packages/client/public/characters/football/mocap/M_StrafeLeft.glb",
    "clipName": "Animation",
    "duration": 0.7333,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 68.1,
    "horizontalMotionRangeMM": 56.9,
    "loopDriftMM": 2.23,
    "originFile": "mixamo-skeleton-animations.glb",
    "originAction": "StrafeLeft",
    "originRig": "mixamo",
    "processing": [
      "facing-normalized",
      "reference-pose-retarget",
      "twist-distributed",
      "sole-ground-solve",
      "pelvis-weight-preserved",
      "hips-in-place-detrended"
    ],
    "frames": 22,
    "fps": 30,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "StrafeRight",
    "file": "packages/client/public/characters/football/mocap/M_StrafeRight.glb",
    "clipName": "Animation",
    "duration": 0.7667,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 68,
    "horizontalMotionRangeMM": 62,
    "loopDriftMM": 0,
    "originFile": "mixamo-skeleton-animations.glb",
    "originAction": "StrafeRight",
    "originRig": "mixamo",
    "processing": [
      "facing-normalized",
      "reference-pose-retarget",
      "twist-distributed",
      "sole-ground-solve",
      "pelvis-weight-preserved",
      "hips-in-place-detrended"
    ],
    "frames": 23,
    "fps": 30,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "ThrowIn",
    "file": "packages/client/public/characters/football/mocap/M_ThrowIn.glb",
    "clipName": "Animation",
    "duration": 2.8,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 0,
    "verticalMotionRangeMM": 0,
    "horizontalMotionRangeMM": 0,
    "loopDriftMM": 0,
    "originFile": "mixamo-skeleton-animations.glb",
    "originAction": "ThrowIn",
    "originRig": "mixamo",
    "processing": [
      "facing-normalized",
      "rest-offset-retarget",
      "hips-in-place"
    ],
    "frames": 84,
    "fps": 30,
    "plantWindows": {
      "left": [
        [
          0,
          0.3667
        ],
        [
          2.6333,
          2.7667
        ]
      ],
      "right": [
        [
          0,
          0.1333
        ],
        [
          2.5333,
          2.7667
        ]
      ]
    },
    "settledTime": 2.6333
  },
  {
    "id": "Run",
    "file": "packages/client/public/characters/football/mocap/Run.glb",
    "clipName": "Animation",
    "duration": 0.6667,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 59.5,
    "horizontalMotionRangeMM": 28,
    "loopDriftMM": 0,
    "originFile": "tools/anim/author/clips/run.py",
    "originAction": null,
    "originRig": "player-master-74",
    "processing": [
      "keyed by hand on the 74-bone master in Blender, not retargeted",
      "every gate in build_clip.py PASSED before promotion",
      "rest skeleton aligned to the master by scripts/align-clip-skeletons.mjs",
      "dead-frame-retimed"
    ],
    "frames": 20,
    "fps": 28.5,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  },
  {
    "id": "Walk",
    "file": "packages/client/public/characters/football/mocap/Walk.glb",
    "clipName": "Animation",
    "duration": 1.2,
    "tracks": 222,
    "movingPositionTracks": 0,
    "verticalMotionTracks": 1,
    "verticalMotionRangeMM": 40,
    "horizontalMotionRangeMM": 42,
    "loopDriftMM": 1.25,
    "originFile": "tools/anim/author/clips/walk.py",
    "originAction": null,
    "originRig": "player-master-74",
    "processing": [
      "keyed by hand on the 74-bone master in Blender, not retargeted",
      "every gate in build_clip.py PASSED before promotion",
      "rest skeleton aligned to the master by scripts/align-clip-skeletons.mjs",
      "dead-frame-retimed"
    ],
    "frames": 36,
    "fps": 29.167,
    "plantWindows": {
      "left": [],
      "right": []
    },
    "settledTime": null
  }
];

export const AUTHORED_FROZEN_BONES: readonly FrozenBone[] = [
  {
    "name": "root",
    "parent": null,
    "parentIndex": -1,
    "pos": [
      0,
      0,
      0
    ],
    "quat": [
      -0.707107,
      0,
      0,
      0.707107
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      -1,
      0,
      0,
      0,
      0,
      0,
      1
    ],
    "deform": false,
    "kind": "root-motion"
  },
  {
    "name": "hips",
    "parent": "root",
    "parentIndex": 0,
    "pos": [
      0,
      -0.004917,
      0.987763
    ],
    "quat": [
      0.568946,
      0,
      0,
      0.822375
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0.935773,
      0.352602,
      0,
      0,
      -0.352602,
      0.935773,
      0,
      0,
      -0.922588,
      -0.352889,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "socket_ball_control",
    "parent": "hips",
    "parentIndex": 1,
    "pos": [
      0,
      -0.894753,
      0.197172
    ],
    "quat": [
      0.179202,
      0,
      0,
      0.983812
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      1,
      0.000001,
      0,
      0,
      -0.000001,
      1,
      0,
      0,
      -0.22,
      -0.504918,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "socket_pelvis",
    "parent": "hips",
    "parentIndex": 1,
    "pos": [
      0,
      -0.045618,
      0.121066
    ],
    "quat": [
      0.179202,
      0,
      0,
      0.983812
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      -0.987763,
      -0.134293,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "spine_01",
    "parent": "hips",
    "parentIndex": 1,
    "pos": [
      0,
      0.093485,
      0
    ],
    "quat": [
      0.249329,
      0,
      0,
      0.968419
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0.989704,
      -0.143131,
      0,
      0,
      0.143131,
      0.989704,
      0,
      0,
      -1.060158,
      0.181658,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "spine_02",
    "parent": "spine_01",
    "parentIndex": 4,
    "pos": [
      0,
      0.070972,
      0
    ],
    "quat": [
      -0.122995,
      0,
      0,
      0.992407
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0.994701,
      0.102807,
      0,
      0,
      -0.102807,
      0.994701,
      0,
      0,
      -1.141254,
      -0.099972,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "chest",
    "parent": "spine_02",
    "parentIndex": 5,
    "pos": [
      0,
      0.066123,
      0
    ],
    "quat": [
      -0.026932,
      0,
      0,
      0.999637
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0.987723,
      0.156217,
      0,
      0,
      -0.156217,
      0.987723,
      0,
      0,
      -1.200243,
      -0.164837,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "clavicle_l",
    "parent": "chest",
    "parentIndex": 6,
    "pos": [
      0.023296,
      0.261548,
      0.093045
    ],
    "quat": [
      0.008377,
      0.051594,
      -0.758706,
      0.649333
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.156594,
      0.98617,
      0.054292,
      0,
      -0.984807,
      -0.160082,
      0.067289,
      0,
      0.07505,
      -0.04293,
      0.996255,
      0,
      1.463252,
      0.21574,
      -0.127391,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "upperarm_l",
    "parent": "clavicle_l",
    "parentIndex": 7,
    "pos": [
      0,
      0.18604,
      0
    ],
    "quat": [
      -0.000588,
      0.016781,
      -0.339,
      0.940637
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.751157,
      0.658963,
      0.039127,
      0,
      -0.657904,
      -0.752171,
      0.037414,
      0,
      0.054085,
      0.002361,
      0.998533,
      0,
      1.11114,
      0.957627,
      -0.080848,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "lowerarm_l",
    "parent": "upperarm_l",
    "parentIndex": 8,
    "pos": [
      0,
      0.264966,
      0
    ],
    "quat": [
      0.387897,
      0.025233,
      0.021098,
      0.921116
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.712198,
      0.502569,
      -0.4901,
      0,
      -0.701566,
      -0.485685,
      0.521454,
      0,
      0.024032,
      0.715216,
      0.69849,
      0,
      1.151652,
      0.404309,
      -0.480814,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "hand_l",
    "parent": "lowerarm_l",
    "parentIndex": 9,
    "pos": [
      0,
      0.270886,
      0
    ],
    "quat": [
      0.10377,
      0.406215,
      -0.015375,
      0.907736
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.085482,
      0.325376,
      -0.941713,
      0,
      -0.883314,
      -0.462001,
      -0.079447,
      0,
      -0.460922,
      0.825037,
      0.326902,
      0,
      1.134683,
      0.175149,
      0.507052,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "index_01_l",
    "parent": "hand_l",
    "parentIndex": 10,
    "pos": [
      -0.014631,
      0.117077,
      0.009601
    ],
    "quat": [
      0.108361,
      0.467295,
      0.00085,
      0.877435
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.757358,
      0.129381,
      -0.640054,
      0,
      -0.479886,
      -0.554459,
      -0.679915,
      0,
      -0.442852,
      0.822092,
      -0.357836,
      0,
      0.245503,
      0.266378,
      1.200218,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "index_02_l",
    "parent": "index_01_l",
    "parentIndex": 11,
    "pos": [
      0,
      0.028763,
      0
    ],
    "quat": [
      0.106219,
      -0.005331,
      0.00705,
      0.994304
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.751163,
      -0.020175,
      -0.659808,
      0,
      -0.495183,
      -0.678187,
      -0.543006,
      0,
      -0.436518,
      0.734612,
      -0.519419,
      0,
      0.263049,
      0.481939,
      1.120624,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "index_03_l",
    "parent": "index_02_l",
    "parentIndex": 12,
    "pos": [
      0,
      0.025773,
      0
    ],
    "quat": [
      0.059963,
      0.001396,
      0.017165,
      0.998052
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.750503,
      -0.124635,
      -0.649008,
      0,
      -0.517845,
      -0.721046,
      -0.460358,
      0,
      -0.410588,
      0.681585,
      -0.605689,
      0,
      0.277783,
      0.57783,
      1.059258,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "middle_01_l",
    "parent": "hand_l",
    "parentIndex": 10,
    "pos": [
      0.004218,
      0.113186,
      -0.012434
    ],
    "quat": [
      0.098832,
      0.407603,
      -0.147543,
      0.895725
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.601959,
      0.221907,
      -0.767074,
      0,
      -0.406127,
      -0.742013,
      -0.533364,
      0,
      -0.687536,
      0.632593,
      -0.356539,
      0,
      0.299752,
      0.477427,
      1.110796,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "middle_02_l",
    "parent": "middle_01_l",
    "parentIndex": 14,
    "pos": [
      0,
      0.037413,
      0
    ],
    "quat": [
      0.048658,
      -0.010396,
      0.038406,
      0.998023
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.598056,
      0.099558,
      -0.795246,
      0,
      -0.474034,
      -0.756142,
      -0.451155,
      0,
      -0.646235,
      0.64679,
      -0.405022,
      0,
      0.359289,
      0.520348,
      1.05711,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "middle_03_l",
    "parent": "middle_02_l",
    "parentIndex": 15,
    "pos": [
      0,
      0.030404,
      0
    ],
    "quat": [
      0.064765,
      0.001186,
      -0.005304,
      0.997886
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.599411,
      0.002359,
      -0.800438,
      0,
      -0.464741,
      -0.813156,
      -0.350419,
      0,
      -0.651707,
      0.582042,
      -0.486318,
      0,
      0.350929,
      0.62629,
      0.985508,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "pinky_01_l",
    "parent": "hand_l",
    "parentIndex": 10,
    "pos": [
      0.038746,
      0.092175,
      -0.033875
    ],
    "quat": [
      0.054266,
      0.279453,
      -0.296728,
      0.911545
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.286873,
      0.280338,
      -0.916032,
      0,
      -0.310858,
      -0.877237,
      -0.365817,
      0,
      -0.90613,
      0.389699,
      -0.164511,
      0,
      0.396402,
      0.657779,
      0.954336,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "pinky_02_l",
    "parent": "pinky_01_l",
    "parentIndex": 17,
    "pos": [
      0,
      0.023731,
      0
    ],
    "quat": [
      0.082771,
      -0.005837,
      0.021425,
      0.996321
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.284385,
      0.11286,
      -0.952044,
      0,
      -0.352706,
      -0.911084,
      -0.213361,
      0,
      -0.891473,
      0.396468,
      -0.219293,
      0,
      0.436951,
      0.764636,
      0.833255,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "pinky_03_l",
    "parent": "pinky_02_l",
    "parentIndex": 18,
    "pos": [
      0,
      0.017255,
      0
    ],
    "quat": [
      -0.005257,
      -0.005012,
      -0.010472,
      0.999919
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.272303,
      0.128708,
      -0.953564,
      0,
      -0.335741,
      -0.916019,
      -0.219515,
      0,
      -0.901737,
      0.379925,
      -0.206222,
      0,
      0.429665,
      0.747677,
      0.836771,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "ring_01_l",
    "parent": "hand_l",
    "parentIndex": 10,
    "pos": [
      0.020024,
      0.104472,
      -0.02599
    ],
    "quat": [
      0.056679,
      0.382125,
      -0.227772,
      0.893805
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.497554,
      0.31953,
      -0.806437,
      0,
      -0.309279,
      -0.803234,
      -0.509079,
      0,
      -0.810424,
      0.502708,
      -0.300828,
      0,
      0.269882,
      0.526244,
      1.087117,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "ring_02_l",
    "parent": "ring_01_l",
    "parentIndex": 20,
    "pos": [
      0,
      0.032967,
      0
    ],
    "quat": [
      0.057997,
      -0.005541,
      0.047183,
      0.997186
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.511845,
      0.175962,
      -0.840864,
      0,
      -0.391363,
      -0.823569,
      -0.41057,
      0,
      -0.764754,
      0.539232,
      -0.352675,
      0,
      0.332728,
      0.587368,
      1.020918,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "ring_03_l",
    "parent": "ring_02_l",
    "parentIndex": 21,
    "pos": [
      0,
      0.027548,
      0
    ],
    "quat": [
      0.063014,
      -0.005836,
      -0.026094,
      0.997654
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.494795,
      0.094617,
      -0.863843,
      0,
      -0.350749,
      -0.887743,
      -0.298138,
      0,
      -0.79508,
      0.45051,
      -0.406065,
      0,
      0.311225,
      0.700365,
      0.937555,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thumb_01_l",
    "parent": "hand_l",
    "parentIndex": 10,
    "pos": [
      -0.016596,
      0.044411,
      0.01563
    ],
    "quat": [
      0.276825,
      0.705787,
      0.432986,
      0.487603
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.718686,
      -0.673593,
      0.172518,
      0,
      -0.012067,
      -0.260152,
      -0.965492,
      0,
      0.69523,
      0.691804,
      -0.195096,
      0,
      -0.541526,
      0.458441,
      1.039525,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thumb_02_l",
    "parent": "thumb_01_l",
    "parentIndex": 23,
    "pos": [
      0,
      0.034139,
      0
    ],
    "quat": [
      0.054294,
      -0.02397,
      -0.187631,
      0.980445
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.921435,
      -0.339724,
      0.188534,
      0,
      0.05948,
      -0.356184,
      -0.932521,
      0,
      0.383953,
      0.870471,
      -0.307994,
      0,
      -0.632309,
      0.314118,
      1.027332,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thumb_03_l",
    "parent": "thumb_02_l",
    "parentIndex": 24,
    "pos": [
      0,
      0.040486,
      0
    ],
    "quat": [
      0.137624,
      0.009059,
      -0.038248,
      0.989704
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.938095,
      -0.202575,
      0.280965,
      0,
      0.111914,
      -0.590383,
      -0.799326,
      0,
      0.327801,
      0.781288,
      -0.531164,
      0,
      -0.679626,
      0.492165,
      0.908835,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "lowerarm_twist_l",
    "parent": "lowerarm_l",
    "parentIndex": 9,
    "pos": [
      0,
      0.135443,
      0
    ],
    "quat": [
      0,
      0,
      0,
      1
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.712198,
      0.502569,
      -0.4901,
      0,
      -0.701566,
      -0.485685,
      0.521454,
      0,
      0.024033,
      0.715216,
      0.69849,
      0,
      1.151652,
      0.268866,
      -0.480814,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "upperarm_twist_l",
    "parent": "upperarm_l",
    "parentIndex": 8,
    "pos": [
      0,
      0.132483,
      0
    ],
    "quat": [
      0,
      0,
      0,
      1
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.751156,
      0.658964,
      0.039127,
      0,
      -0.657905,
      -0.752171,
      0.037413,
      0,
      0.054084,
      0.002361,
      0.998533,
      0,
      1.111141,
      0.825144,
      -0.080848,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "clavicle_r",
    "parent": "chest",
    "parentIndex": 6,
    "pos": [
      -0.023296,
      0.261548,
      0.093045
    ],
    "quat": [
      0.008377,
      -0.051594,
      0.758706,
      0.649333
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.156594,
      -0.98617,
      -0.054292,
      0,
      0.984808,
      -0.160082,
      0.067289,
      0,
      -0.07505,
      -0.04293,
      0.996255,
      0,
      -1.463253,
      0.21574,
      -0.127391,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "upperarm_r",
    "parent": "clavicle_r",
    "parentIndex": 28,
    "pos": [
      0,
      0.186039,
      0
    ],
    "quat": [
      -0.000588,
      -0.016781,
      0.339,
      0.940637
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.751157,
      -0.658964,
      -0.039127,
      0,
      0.657905,
      -0.752171,
      0.037414,
      0,
      -0.054085,
      0.002361,
      0.998533,
      0,
      -1.111141,
      0.957627,
      -0.080848,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "lowerarm_r",
    "parent": "upperarm_r",
    "parentIndex": 29,
    "pos": [
      0,
      0.264966,
      0
    ],
    "quat": [
      0.387897,
      -0.025233,
      -0.021098,
      0.921116
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.712199,
      -0.502569,
      0.4901,
      0,
      0.701567,
      -0.485685,
      0.521454,
      0,
      -0.024032,
      0.715216,
      0.69849,
      0,
      -1.151652,
      0.404309,
      -0.480813,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "hand_r",
    "parent": "lowerarm_r",
    "parentIndex": 30,
    "pos": [
      0,
      0.270886,
      0
    ],
    "quat": [
      0.10377,
      -0.406215,
      0.015375,
      0.907736
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.085482,
      -0.325377,
      0.941713,
      0,
      0.883314,
      -0.462,
      -0.079448,
      0,
      0.460922,
      0.825037,
      0.326903,
      0,
      -1.134683,
      0.175148,
      0.507053,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "index_01_r",
    "parent": "hand_r",
    "parentIndex": 31,
    "pos": [
      0.014632,
      0.117077,
      0.009601
    ],
    "quat": [
      0.108362,
      -0.467294,
      -0.00085,
      0.877435
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.757358,
      -0.12938,
      0.640054,
      0,
      0.479886,
      -0.55446,
      -0.679914,
      0,
      0.442852,
      0.822092,
      -0.357837,
      0,
      -0.245503,
      0.26638,
      1.200219,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "index_02_r",
    "parent": "index_01_r",
    "parentIndex": 32,
    "pos": [
      0,
      0.028763,
      0
    ],
    "quat": [
      0.106218,
      0.005331,
      -0.00705,
      0.994304
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.751163,
      0.020176,
      0.659809,
      0,
      0.495184,
      -0.678186,
      -0.543007,
      0,
      0.436518,
      0.734613,
      -0.519419,
      0,
      -0.263049,
      0.481938,
      1.120624,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "index_03_r",
    "parent": "index_02_r",
    "parentIndex": 33,
    "pos": [
      0,
      0.025773,
      0
    ],
    "quat": [
      0.059964,
      -0.001396,
      -0.017164,
      0.998052
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.750503,
      0.124636,
      0.649008,
      0,
      0.517844,
      -0.721047,
      -0.460357,
      0,
      0.410589,
      0.681584,
      -0.60569,
      0,
      -0.277783,
      0.577832,
      1.059258,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "middle_01_r",
    "parent": "hand_r",
    "parentIndex": 31,
    "pos": [
      -0.004218,
      0.113186,
      -0.012433
    ],
    "quat": [
      0.098832,
      -0.407603,
      0.147542,
      0.895725
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.601959,
      -0.221907,
      0.767074,
      0,
      0.406129,
      -0.742011,
      -0.533366,
      0,
      0.687535,
      0.632595,
      -0.356537,
      0,
      -0.299752,
      0.477425,
      1.110797,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "middle_02_r",
    "parent": "middle_01_r",
    "parentIndex": 35,
    "pos": [
      0,
      0.037413,
      0
    ],
    "quat": [
      0.048659,
      0.010396,
      -0.038405,
      0.998023
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.598056,
      -0.099558,
      0.795246,
      0,
      0.474035,
      -0.756142,
      -0.451155,
      0,
      0.646235,
      0.646791,
      -0.405021,
      0,
      -0.359289,
      0.520348,
      1.05711,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "middle_03_r",
    "parent": "middle_02_r",
    "parentIndex": 36,
    "pos": [
      0,
      0.030404,
      0
    ],
    "quat": [
      0.064765,
      -0.001186,
      0.005304,
      0.997886
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.599411,
      -0.002358,
      0.800438,
      0,
      0.464741,
      -0.813156,
      -0.350419,
      0,
      0.651707,
      0.582042,
      -0.486318,
      0,
      -0.350929,
      0.62629,
      0.985508,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "pinky_01_r",
    "parent": "hand_r",
    "parentIndex": 31,
    "pos": [
      -0.038746,
      0.092175,
      -0.033875
    ],
    "quat": [
      0.054266,
      -0.279452,
      0.296727,
      0.911545
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.286873,
      -0.280339,
      0.916031,
      0,
      0.310858,
      -0.877237,
      -0.365818,
      0,
      0.906129,
      0.3897,
      -0.16451,
      0,
      -0.396403,
      0.657778,
      0.954337,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "pinky_02_r",
    "parent": "pinky_01_r",
    "parentIndex": 38,
    "pos": [
      0,
      0.023731,
      0
    ],
    "quat": [
      0.082773,
      0.005837,
      -0.021424,
      0.996321
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.284385,
      -0.112859,
      0.952044,
      0,
      0.352707,
      -0.911085,
      -0.21336,
      0,
      0.891472,
      0.396469,
      -0.219293,
      0,
      -0.436952,
      0.764636,
      0.833255,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "pinky_03_r",
    "parent": "pinky_02_r",
    "parentIndex": 39,
    "pos": [
      0,
      0.017256,
      0
    ],
    "quat": [
      -0.005257,
      0.005012,
      0.010472,
      0.999919
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.272303,
      -0.128707,
      0.953565,
      0,
      0.335741,
      -0.916019,
      -0.219515,
      0,
      0.901736,
      0.379926,
      -0.206223,
      0,
      -0.429665,
      0.747677,
      0.836771,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "ring_01_r",
    "parent": "hand_r",
    "parentIndex": 31,
    "pos": [
      -0.020024,
      0.104472,
      -0.02599
    ],
    "quat": [
      0.056679,
      -0.382125,
      0.227772,
      0.893805
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.497554,
      -0.319531,
      0.806437,
      0,
      0.309279,
      -0.803233,
      -0.50908,
      0,
      0.810424,
      0.502709,
      -0.300828,
      0,
      -0.269882,
      0.526243,
      1.087118,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "ring_02_r",
    "parent": "ring_01_r",
    "parentIndex": 41,
    "pos": [
      0,
      0.032967,
      0
    ],
    "quat": [
      0.057997,
      0.005541,
      -0.047182,
      0.997186
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.511845,
      -0.175963,
      0.840864,
      0,
      0.391362,
      -0.82357,
      -0.410571,
      0,
      0.764755,
      0.539231,
      -0.352675,
      0,
      -0.332727,
      0.587368,
      1.020919,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "ring_03_r",
    "parent": "ring_02_r",
    "parentIndex": 42,
    "pos": [
      0,
      0.027548,
      0
    ],
    "quat": [
      0.063015,
      0.005836,
      0.026094,
      0.997654
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.494795,
      -0.094616,
      0.863843,
      0,
      0.350749,
      -0.887744,
      -0.298137,
      0,
      0.79508,
      0.450509,
      -0.406065,
      0,
      -0.311224,
      0.700366,
      0.937554,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thumb_01_r",
    "parent": "hand_r",
    "parentIndex": 31,
    "pos": [
      0.016596,
      0.044411,
      0.01563
    ],
    "quat": [
      0.276825,
      -0.705787,
      -0.432985,
      0.487603
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.718687,
      0.673592,
      -0.172518,
      0,
      0.012067,
      -0.260153,
      -0.965492,
      0,
      -0.695229,
      0.691805,
      -0.195096,
      0,
      0.541526,
      0.458442,
      1.039525,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thumb_02_r",
    "parent": "thumb_01_r",
    "parentIndex": 44,
    "pos": [
      0,
      0.034139,
      0
    ],
    "quat": [
      0.054293,
      0.02397,
      0.18763,
      0.980445
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.921435,
      0.339725,
      -0.188534,
      0,
      -0.05948,
      -0.356184,
      -0.932521,
      0,
      -0.383953,
      0.870471,
      -0.307993,
      0,
      0.632309,
      0.314118,
      1.027332,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thumb_03_r",
    "parent": "thumb_02_r",
    "parentIndex": 45,
    "pos": [
      0,
      0.040486,
      0
    ],
    "quat": [
      0.137624,
      -0.009059,
      0.038248,
      0.989704
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.938095,
      0.202575,
      -0.280964,
      0,
      -0.111913,
      -0.590383,
      -0.799327,
      0,
      -0.3278,
      0.781288,
      -0.531164,
      0,
      0.679626,
      0.492165,
      0.908835,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "lowerarm_twist_r",
    "parent": "lowerarm_r",
    "parentIndex": 30,
    "pos": [
      0,
      0.135443,
      0
    ],
    "quat": [
      0,
      0,
      0,
      1
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.712199,
      -0.502569,
      0.4901,
      0,
      0.701566,
      -0.485685,
      0.521454,
      0,
      -0.024032,
      0.715216,
      0.69849,
      0,
      -1.151652,
      0.268866,
      -0.480813,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "upperarm_twist_r",
    "parent": "upperarm_r",
    "parentIndex": 29,
    "pos": [
      0,
      0.132483,
      0
    ],
    "quat": [
      0,
      0,
      0,
      1
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -0.751157,
      -0.658964,
      -0.039127,
      0,
      0.657905,
      -0.752171,
      0.037414,
      0,
      -0.054085,
      0.002361,
      0.998533,
      0,
      -1.111141,
      0.825144,
      -0.080848,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "neck",
    "parent": "chest",
    "parentIndex": 6,
    "pos": [
      0,
      0.337907,
      0.089781
    ],
    "quat": [
      0.244854,
      0,
      0,
      0.96956
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0.94346,
      -0.331487,
      0,
      0,
      0.331487,
      0.94346,
      0,
      0,
      -1.474608,
      0.506229,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "head",
    "parent": "neck",
    "parentIndex": 49,
    "pos": [
      0,
      0.108528,
      0
    ],
    "quat": [
      -0.187825,
      0,
      0,
      0.982203
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      0.9992,
      0.040005,
      0,
      0,
      -0.040005,
      0.9992,
      0,
      0,
      -1.658216,
      -0.113609,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "eye_l",
    "parent": "head",
    "parentIndex": 50,
    "pos": [
      0.030183,
      0.035087,
      0.083709
    ],
    "quat": [
      0,
      0.692819,
      0.721112,
      0
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -1,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      1,
      0,
      0,
      0.030183,
      -0.12942,
      -1.699841,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "eye_r",
    "parent": "head",
    "parentIndex": 50,
    "pos": [
      -0.030183,
      0.035087,
      0.083709
    ],
    "quat": [
      0,
      0.692819,
      0.721112,
      0
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      -1,
      0,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      1,
      0,
      0,
      -0.030183,
      -0.12942,
      -1.699841,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "jaw",
    "parent": "head",
    "parentIndex": 50,
    "pos": [
      0,
      0.026893,
      -0.025104
    ],
    "quat": [
      0.827732,
      0,
      0,
      0.561124
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0.000001,
      0,
      0.000001,
      -0.332822,
      -0.94299,
      0,
      0,
      0.94299,
      -0.332822,
      0,
      -0.000001,
      0.541747,
      1.598104,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "socket_head",
    "parent": "head",
    "parentIndex": 50,
    "pos": [
      0,
      0.109383,
      0.108203
    ],
    "quat": [
      0.020006,
      0,
      0,
      0.9998
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      -1.775058,
      -0.150922,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "socket_chest",
    "parent": "chest",
    "parentIndex": 6,
    "pos": [
      0,
      0.033244,
      0.173887
    ],
    "quat": [
      0.07835,
      0,
      0,
      0.996926
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      -1.271258,
      -0.141873,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "thigh_l",
    "parent": "hips",
    "parentIndex": 1,
    "pos": [
      0.106813,
      -0.001315,
      -0.011461
    ],
    "quat": [
      -0.964462,
      -0.082304,
      0.217888,
      0.124758
    ],
    "scale": [
      1,
      1,
      0.999998
    ],
    "inverseBind": [
      0.891502,
      0.104392,
      -0.440827,
      0,
      0.058484,
      -0.991466,
      -0.116516,
      0,
      -0.449226,
      0.078093,
      -0.89,
      0,
      -0.155084,
      0.963373,
      0.156807,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "calf_l",
    "parent": "thigh_l",
    "parentIndex": 56,
    "pos": [
      0,
      0.461297,
      0
    ],
    "quat": [
      0.056055,
      0.242853,
      -0.018287,
      0.96827
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.993116,
      0.115573,
      0.019123,
      0,
      0.1147,
      -0.992522,
      0.041777,
      0,
      0.02381,
      -0.039296,
      -0.998945,
      0,
      -0.214865,
      0.504499,
      0.005747,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "foot_l",
    "parent": "calf_l",
    "parentIndex": 57,
    "pos": [
      0,
      0.454549,
      0
    ],
    "quat": [
      -0.539836,
      -0.031917,
      0.02117,
      0.840899
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.998889,
      0.029585,
      0.036702,
      0,
      0.046111,
      -0.451262,
      -0.8912,
      0,
      -0.009803,
      0.891901,
      -0.452125,
      0,
      -0.210558,
      0.015813,
      0.064112,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "socket_foot_l",
    "parent": "foot_l",
    "parentIndex": 58,
    "pos": [
      0.001614,
      0.07137,
      -0.031192
    ],
    "quat": [
      -0.851803,
      -0.022216,
      -0.007896,
      0.523332
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1.000001,
      0.000001,
      0.000003,
      0,
      0,
      1,
      -0.000001,
      0,
      -0.000001,
      0.000002,
      1.000001,
      0,
      -0.210082,
      -0.069648,
      -0.090562,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "toe_l",
    "parent": "foot_l",
    "parentIndex": 58,
    "pos": [
      0,
      0.145274,
      0
    ],
    "quat": [
      -0.252559,
      -0.262792,
      0.107283,
      0.925011
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.863583,
      -0.05975,
      -0.500655,
      0,
      -0.495765,
      0.080318,
      -0.864735,
      0,
      0.091881,
      0.994978,
      0.039738,
      0,
      -0.191814,
      -0.129695,
      0.107666,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "socket_toe_l",
    "parent": "toe_l",
    "parentIndex": 60,
    "pos": [
      0.000919,
      0.083833,
      0.000397
    ],
    "quat": [
      -0.660213,
      0.210355,
      0.154788,
      0.704209
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1.000001,
      0.000001,
      0.000002,
      0,
      -0.000001,
      1,
      -0.000001,
      0,
      0,
      0.000001,
      1.000001,
      0,
      -0.207386,
      -0.014359,
      -0.225902,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "socket_knee_l",
    "parent": "calf_l",
    "parentIndex": 57,
    "pos": [
      0.00131,
      -0.002161,
      -0.054942
    ],
    "quat": [
      0.998071,
      0.05768,
      0.010755,
      0.020307
    ],
    "scale": [
      0.999992,
      1,
      0.999999
    ],
    "inverseBind": [
      1.000009,
      0,
      0.000006,
      0,
      0,
      1.000001,
      -0.000001,
      0,
      0.000002,
      0.000001,
      1.000002,
      0,
      -0.15497,
      -0.525131,
      -0.085681,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "socket_tackle_l",
    "parent": "calf_l",
    "parentIndex": 57,
    "pos": [
      0.001071,
      0.35278,
      -0.044953
    ],
    "quat": [
      0.998071,
      0.05768,
      0.010753,
      0.020307
    ],
    "scale": [
      0.999995,
      1,
      0.999999
    ],
    "inverseBind": [
      1.000006,
      0.000001,
      0,
      0,
      -0.000001,
      1,
      -0.000001,
      0,
      0.000005,
      0.000002,
      1.000002,
      0,
      -0.195947,
      -0.173234,
      -0.061749,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "thigh_twist_l",
    "parent": "thigh_l",
    "parentIndex": 56,
    "pos": [
      0,
      0.230649,
      0
    ],
    "quat": [
      0,
      0,
      0,
      1
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.891502,
      0.104392,
      -0.440827,
      0,
      0.058484,
      -0.991466,
      -0.116516,
      0,
      -0.449226,
      0.078093,
      -0.89,
      0,
      -0.155084,
      0.732724,
      0.156807,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "thigh_r",
    "parent": "hips",
    "parentIndex": 1,
    "pos": [
      -0.106813,
      -0.001315,
      -0.011461
    ],
    "quat": [
      -0.964462,
      0.082304,
      -0.217887,
      0.124758
    ],
    "scale": [
      1,
      1,
      1.000001
    ],
    "inverseBind": [
      0.891502,
      -0.104393,
      0.440824,
      0,
      -0.058484,
      -0.991465,
      -0.116516,
      0,
      0.449226,
      0.078093,
      -0.889998,
      0,
      0.155084,
      0.963373,
      0.156806,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "calf_r",
    "parent": "thigh_r",
    "parentIndex": 65,
    "pos": [
      0,
      0.461297,
      0
    ],
    "quat": [
      0.056055,
      -0.242853,
      0.018287,
      0.96827
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.993114,
      -0.115574,
      -0.019125,
      0,
      -0.1147,
      -0.992521,
      0.041777,
      0,
      -0.023809,
      -0.039295,
      -0.998944,
      0,
      0.214865,
      0.504499,
      0.005746,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "foot_r",
    "parent": "calf_r",
    "parentIndex": 66,
    "pos": [
      0,
      0.454549,
      0
    ],
    "quat": [
      -0.539835,
      0.031917,
      -0.02117,
      0.840899
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.998888,
      -0.029583,
      -0.036704,
      0,
      -0.046111,
      -0.451264,
      -0.891198,
      0,
      0.009803,
      0.8919,
      -0.452126,
      0,
      0.210558,
      0.015813,
      0.064112,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "socket_foot_r",
    "parent": "foot_r",
    "parentIndex": 67,
    "pos": [
      -0.001614,
      0.07137,
      -0.031192
    ],
    "quat": [
      -0.851804,
      0.022216,
      0.007896,
      0.523331
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      1,
      -0.000001,
      0,
      0.000001,
      0.000001,
      1,
      0,
      0.210081,
      -0.069647,
      -0.090561,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "toe_r",
    "parent": "foot_r",
    "parentIndex": 67,
    "pos": [
      0,
      0.145274,
      0
    ],
    "quat": [
      -0.25256,
      0.262792,
      -0.107283,
      0.925011
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.863582,
      0.059753,
      0.500654,
      0,
      0.495765,
      0.080317,
      -0.864735,
      0,
      -0.091881,
      0.994976,
      0.039738,
      0,
      0.191814,
      -0.129695,
      0.107666,
      1
    ],
    "deform": true,
    "kind": "deform"
  },
  {
    "name": "socket_toe_r",
    "parent": "toe_r",
    "parentIndex": 69,
    "pos": [
      -0.000919,
      0.083833,
      0.000397
    ],
    "quat": [
      -0.660214,
      -0.210355,
      -0.154788,
      0.704208
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      1,
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0.000001,
      0,
      1,
      0,
      0.207386,
      -0.014359,
      -0.225901,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "socket_knee_r",
    "parent": "calf_r",
    "parentIndex": 66,
    "pos": [
      -0.00131,
      -0.002161,
      -0.054942
    ],
    "quat": [
      0.998071,
      -0.05768,
      -0.010756,
      0.020307
    ],
    "scale": [
      0.99999,
      1,
      0.999999
    ],
    "inverseBind": [
      1.00001,
      0.000001,
      -0.000006,
      0,
      0,
      1,
      0,
      0,
      -0.000001,
      0,
      1.000001,
      0,
      0.154971,
      -0.525131,
      -0.085681,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "socket_tackle_r",
    "parent": "calf_r",
    "parentIndex": 66,
    "pos": [
      -0.001071,
      0.35278,
      -0.044952
    ],
    "quat": [
      0.998071,
      -0.05768,
      -0.010751,
      0.020307
    ],
    "scale": [
      1.000006,
      1,
      1.000001
    ],
    "inverseBind": [
      0.999994,
      -0.000001,
      0.000009,
      0,
      0.000001,
      1,
      -0.000001,
      0,
      -0.000004,
      0.000001,
      0.999999,
      0,
      0.195944,
      -0.173234,
      -0.061746,
      1
    ],
    "deform": false,
    "kind": "socket"
  },
  {
    "name": "thigh_twist_r",
    "parent": "thigh_r",
    "parentIndex": 65,
    "pos": [
      0,
      0.230649,
      0
    ],
    "quat": [
      0,
      0,
      0,
      1
    ],
    "scale": [
      1,
      1,
      1
    ],
    "inverseBind": [
      0.891502,
      -0.104393,
      0.440824,
      0,
      -0.058484,
      -0.991465,
      -0.116516,
      0,
      0.449226,
      0.078093,
      -0.889998,
      0,
      0.155084,
      0.732724,
      0.156806,
      1
    ],
    "deform": true,
    "kind": "deform"
  }
];

/** Name → row, built once. */
export const AUTHORED_FROZEN_BONE_BY_NAME: ReadonlyMap<string, FrozenBone> = new Map(
  AUTHORED_FROZEN_BONES.map((b) => [b.name, b]),
);
